import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import { propsToDom } from 'core/helpers';
import { Panel } from 'components/Panel';
import { rem } from 'styles/utils';
import { Label } from 'styles/typography';
import { Container } from 'components/Container';
import { LoopClones, Block as LoopClonesBlock } from 'components/LoopClones';
import { motion, useScroll, useTransform } from 'framer-motion';
import classNames from 'classnames';
import useOffsetSize from 'hooks/useOffsetSize';
import { useGlobalContext } from 'store/GlobalProvider';

const LogosComponent = ({ primary, items, slice_type, next_slice_type, ...others }) => {
  const { background, label } = primary;
  const hasLabel = !!label?.text;
  const $ref = useRef(null);
  const $container = useRef(null);
  const { wh } = useGlobalContext();
  const [{ top }] = useOffsetSize($ref);
  const [{ height: stickyHeight }, $sticky] = useOffsetSize();
  const [scrollAmount, setScrollAmout] = useState(0);
  const { scrollY } = useScroll();

  const lines = [
    items.slice(0, Math.round(items.length / 2)),
    items.slice(Math.round(items.length / 2), items.length),
  ];

  const xReverse = useTransform(
    scrollY,
    [top - wh, top + wh + scrollAmount],
    [-wh / 2, scrollAmount + wh / 2]
  );

  const x = useTransform(xReverse, (xReverse) => -xReverse);

  useEffect(() => {
    const $logos = $container.current.querySelectorAll('.logos:first-child');
    const observer = new ResizeObserver((entries) => {
      const maxWidth = Math.max(...entries.map((entry) => entry.contentRect.width));
      setScrollAmout(maxWidth);
    });

    $logos.forEach((node) => observer.observe(node));

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Panel
      ref={$ref}
      {...propsToDom(others)}
      id={slice_type}
      nextId={next_slice_type}
      background={background}
      style={{ height: `calc(${scrollAmount}px + ${stickyHeight}px)` }}
    >
      <Sticky ref={$sticky}>
        <Container>{hasLabel && <Label>{primary.label.text}</Label>}</Container>
        <Lines ref={$container}>
          {lines.map((line, index) => {
            const reversed = index % 2 === 1;

            const $logos = line.map(({ logo }, index) => {
              const isSvg = logo.url.includes('.svg');
              return (
                <img
                  key={index}
                  src={logo.url}
                  alt={logo.alt}
                  style={{ width: rem(logo.dimensions.width / (isSvg ? 1 : 2)) }}
                />
              );
            });

            return (
              <LinesLogos
                key={index}
                className={classNames({ reversed })}
                style={{ x: reversed ? x : xReverse }}
              >
                {new Array(3).fill(0).map((_, index) => (
                  <div key={index} className="logos">
                    {$logos}
                  </div>
                ))}
              </LinesLogos>
            );
          })}
        </Lines>
      </Sticky>
    </Panel>
  );
};

const Sticky = styled.div`
  --sticky-top: calc(var(--scroll-padding-top) + var(--header-height) + ${rem(30)});
  position: sticky;
  width: 100%;
  top: var(--sticky-top);
  height: calc(var(--viewport-height) - var(--sticky-top));
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${Container} {
    flex: 0 0 auto;
  }
`;

const Lines = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  margin: auto 0;
  flex: 1 1 auto;
`;

const LinesLogos = styled(motion.div)`
  position: relative;
  margin: ${rem(20)} 0;

  div {
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: var(--logos-gap);
    min-width: 100%;

    &:nth-child(2) {
      position: absolute;
      top: 0;
      right: calc((var(--logos-gap) + 100%) * 1);
    }

    &:nth-child(3) {
      position: absolute;
      top: 0;
      right: calc((var(--logos-gap) + 100%) * 2);
    }
  }

  &.reversed div {
    &:nth-child(2) {
      right: auto;
      left: calc((var(--logos-gap) + 100%) * 1);
    }

    &:nth-child(3) {
      right: auto;
      left: calc((var(--logos-gap) + 100%) * 2);
    }
  }

  &:last-child {
    flex-direction: row-reverse;
  }
`;

export const Logos = styled(LogosComponent)`
  --panel-padding: ${rem(100)};
  --logos-gap: ${rem(200)};
  box-sizing: content-box;

  ${Label} {
    margin-bottom: ${rem(30)};
  }

  ${LoopClones} + ${LoopClones} {
    margin-top: ${rem(75)};
  }

  ${LoopClonesBlock} {
    flex-direction: row;
    gap: ${rem(200)};
    padding: 0 ${rem(100)};

    &:not(:last-child) {
      /* margin-right: ${rem(200)}; */
    }
  }
`;

export const query = graphql`
  fragment SliceLogosHome on PrismicHomeDataBodyLogos {
    id
    slice_type
    primary {
      background
      label {
        text
      }
    }
    items {
      logo {
        url
        alt
        dimensions {
          width
          height
        }
      }
    }
  }
`;
